// Pure rules for the site-admin Past data requests page: which steps a request can take
// next, the summary filters, and the supplier playbook editor's form mapping.
// Dependency-free so it can be unit-tested with
// `node --test src/features/dashboard/site-admin/meter-history/historyAdminDisplay.test.js`.

/**
 * The steps staff can take on a request in this status (mirrors backfills.py):
 * send (To send), chase (waiting), upload (sent, received or imported), close as refused
 * or cancelled (any open request), and reopen (refused or cancelled).
 */
export function nextActions(status) {
    return {
        send: status === 'new',
        chase: status === 'requested' || status === 'chased',
        upload: ['requested', 'chased', 'received', 'imported'].includes(
            status
        ),
        close: ['new', 'requested', 'chased', 'received'].includes(status),
        reopen: status === 'refused' || status === 'cancelled',
        editDue: status === 'requested' || status === 'chased'
    }
}

/** The nav badge: requests waiting on Power (to send, or overdue to chase). 0 hides it. */
export function historyNavBadgeCount(summary) {
    if (!summary) return 0
    return (Number(summary.to_send) || 0) + (Number(summary.overdue) || 0)
}

/**
 * Where the request goes: its own address when staff set one, otherwise the supplier
 * playbook's (mirrors backfills.supplier_address). `source` says which, '' when neither.
 */
export function sendToAddress(request) {
    const own = String(request?.supplier_email ?? '').trim()
    if (own) return { address: own, source: 'request' }
    const playbook = String(request?.playbook_email ?? '').trim()
    if (playbook) return { address: playbook, source: 'playbook' }
    return { address: '', source: '' }
}

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** '' when blank (blank is allowed) or a plausible address, else the message to show. */
export function optionalEmailError(value) {
    const email = String(value ?? '').trim()
    return email && !EMAIL_PATTERN.test(email) ? 'Not an email address.' : ''
}

/** Data feeds' Files tab with this file's details open. */
export function feedFileHref(fileId) {
    return `/site-admin/meter-feeds?tab=files&file=${encodeURIComponent(fileId)}`
}

/** A due date can be cleared while the request waits on the supplier and has one. */
export function canClearDue(request) {
    return Boolean(request?.due_on) && nextActions(request?.status).editDue
}

// The summary chips, each a filter on the list.
export const SUMMARY_FILTERS = [
    { key: 'to_send', label: 'To send', status: 'new', color: 'info' },
    { key: 'waiting', label: 'Waiting', status: 'waiting', color: 'info' },
    { key: 'overdue', label: 'Overdue', status: 'overdue', color: 'error' },
    { key: 'received', label: 'Received', status: 'received', color: 'info' }
]

export const REQUEST_STATUS_OPTIONS = [
    { value: 'open', label: 'Open' },
    { value: 'waiting', label: 'Waiting on supplier' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'new', label: 'To send' },
    { value: 'requested', label: 'Requested' },
    { value: 'chased', label: 'Chased' },
    { value: 'received', label: 'Received' },
    { value: 'imported', label: 'Imported' },
    { value: 'refused', label: 'Refused' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'closed', label: 'Closed' },
    { value: '', label: 'Any status' }
]

export const SUPPLIER_UTILITY_OPTIONS = [
    { value: 'electricity', label: 'Electricity' },
    { value: 'gas', label: 'Gas' },
    { value: 'mixed', label: 'Electricity and gas' }
]

export const SUPPLIER_UTILITY_LABELS = Object.fromEntries(
    SUPPLIER_UTILITY_OPTIONS.map((option) => [option.value, option.label])
)

export const THIRD_PARTY_LOA_OPTIONS = [
    { value: 'yes', label: "Yes: they act on Power's Letter of Authority" },
    { value: 'no', label: 'No: the customer must ask them' },
    { value: 'unknown', label: 'Not known yet' }
]

export const THIRD_PARTY_LOA_COLORS = {
    yes: 'success',
    no: 'warning',
    unknown: 'default'
}

// Model defaults (meter_data/models.py SupplierPlaybook), for a new supplier.
export const SUPPLIER_DEFAULTS = {
    name: '',
    utilities: 'mixed',
    request_email: '',
    portal_url: '',
    third_party_loa: 'unknown',
    how_to_request: '',
    expected_format: '',
    turnaround_days: 10,
    feed_source: '',
    reply_senders: '',
    notes: '',
    checked_on: ''
}

const SUPPLIER_FIELDS = Object.keys(SUPPLIER_DEFAULTS)

/** The editor's state for a saved supplier (blank for a new one). */
export function supplierToForm(supplier) {
    const form = { ...SUPPLIER_DEFAULTS }
    if (!supplier) return form
    for (const field of SUPPLIER_FIELDS) {
        if (supplier[field] !== undefined && supplier[field] !== null) {
            form[field] = supplier[field]
        }
    }
    return form
}

/** The JSON body for create/update: no feed source or check date is null, days a number. */
export function supplierFormToPayload(form) {
    const payload = {}
    for (const field of SUPPLIER_FIELDS) {
        payload[field] =
            typeof form[field] === 'string' ? form[field].trim() : form[field]
    }
    payload.feed_source = payload.feed_source || null
    payload.checked_on = payload.checked_on || null
    const days = Number.parseInt(payload.turnaround_days, 10)
    payload.turnaround_days = Number.isNaN(days)
        ? payload.turnaround_days
        : days
    return payload
}

/** Client checks that mirror the model's, so slips show before saving. */
export function validateSupplierForm(form) {
    const errors = {}
    if (!String(form.name ?? '').trim())
        errors.name = 'Give the supplier a name.'
    const emailError = optionalEmailError(form.request_email)
    if (emailError) errors.request_email = emailError
    const portal = String(form.portal_url ?? '').trim()
    if (portal && !/^https?:\/\//i.test(portal)) {
        errors.portal_url = 'Start with https://'
    }
    const days = Number.parseInt(form.turnaround_days, 10)
    if (Number.isNaN(days) || days < 1) {
        errors.turnaround_days = 'At least 1 working day.'
    }
    return errors
}
